import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ArrowLeft, Trophy } from "@phosphor-icons/react";
import { api } from "../lib/api";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

const ROWS = [
  { key: "sent_at", label: "Sent", fmt: fmtDate },
  { key: "recipients", label: "Recipients", fmt: v => v.toLocaleString() },
  { key: "sent", label: "Emails sent", fmt: v => v.toLocaleString() },
  { key: "unique_opens", label: "Unique opens", fmt: v => v.toLocaleString() },
  { key: "open_rate", label: "Open rate", fmt: v => `${v}%`, best: "high" },
  { key: "unique_clicks", label: "Unique clicks", fmt: v => v.toLocaleString() },
  { key: "click_rate", label: "Click rate", fmt: v => `${v}%`, best: "high" },
  { key: "ctor", label: "Click-to-open", fmt: v => `${v}%`, best: "high" },
  { key: "bounce_rate", label: "Bounce rate", fmt: v => `${v}%`, best: "low" },
  { key: "unsubscribed", label: "Unsubscribes", fmt: v => v.toLocaleString(), best: "low" },
];

export default function CampaignComparePage() {
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const [data, setData] = useState(null);

  useEffect(() => {
    if (ids) api.compareCampaigns(ids).then(setData).catch(() => setData({ campaigns: [] }));
  }, [ids]);

  const items = data?.campaigns || [];

  const bestIn = (row) => {
    if (!row.best) return null;
    const vals = items.map(c => c[row.key]);
    const target = row.best === "high" ? Math.max(...vals) : Math.min(...vals);
    if (vals.every(v => v === vals[0])) return null;
    return target;
  };

  const chartData = items.map(c => ({ name: c.name.length > 22 ? c.name.slice(0, 22) + "…" : c.name, "Open rate": c.open_rate, "Click rate": c.click_rate }));

  return (
    <div className="space-y-5" data-testid="compare-page">
      <Link to="/analytics" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950"><ArrowLeft size={14}/> Analytics</Link>
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Campaign comparison</h2>
        <p className="text-sm text-zinc-500 mt-0.5">Side-by-side performance. The best value in each row gets a green highlight.</p>
      </div>

      {!data && <div className="text-sm text-zinc-400 py-10 text-center">Loading…</div>}
      {data && items.length < 2 && <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">Pick at least two campaigns on the Analytics page to compare.</div>}

      {items.length >= 2 && (
        <>
          <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="compare-table">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left px-3 py-3 font-medium text-xs text-zinc-500 w-36">Metric</th>
                  {items.map(c => (
                    <th key={c.id} className="text-left px-3 py-3">
                      <Link to={`/campaigns/${c.id}`} className="font-medium text-zinc-900 hover:underline block truncate max-w-[220px]">{c.name}</Link>
                      <span className="block text-[11px] font-normal text-zinc-500 truncate max-w-[220px]">“{c.subject}”{c.ab_enabled ? " (A/B)" : ""}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(row => {
                  const best = bestIn(row);
                  return (
                    <tr key={row.key} className="border-b border-zinc-100">
                      <td className="px-3 py-2.5 text-xs text-zinc-500">{row.label}</td>
                      {items.map(c => {
                        const isBest = best !== null && c[row.key] === best;
                        return (
                          <td key={c.id} className={`px-3 py-2.5 ${isBest ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-zinc-800"}`}>
                            {row.fmt(c[row.key])}
                            {isBest && <Trophy size={12} weight="fill" className="inline ml-1.5 text-emerald-600 align-[-1px]"/>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <div className="text-sm font-medium mb-3">Open & click rate</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5"/>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }}/>
                  <YAxis tick={{ fontSize: 10 }} unit="%"/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e4e4e7" }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                  <Bar dataKey="Open rate" fill="#6366f1" radius={[4, 4, 0, 0]}/>
                  <Bar dataKey="Click rate" fill="#f59e0b" radius={[4, 4, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <p className="text-[11px] text-zinc-400">Opens are pixel-based and under-count when images are blocked — compare trends rather than absolutes. Scanner activity is excluded.</p>
        </>
      )}
    </div>
  );
}
